import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { MicRecorder, extensionFor } from "../../lib/mic";

/**
 * Renders a mic button that records audio → sends to /api/audio/transcribe → onTranscript(text).
 */
export default function MicButton({ onTranscript, disabled = false, size = "sm" }) {
  const recorderRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | recording | processing
  const supported = MicRecorder.isSupported();

  if (!supported) return null;

  const start = async () => {
    try {
      const rec = new MicRecorder();
      await rec.start();
      recorderRef.current = rec;
      setState("recording");
    } catch (e) {
      toast.error("Microphone access denied.");
      setState("idle");
    }
  };

  const stop = async () => {
    const rec = recorderRef.current;
    if (!rec) return;
    setState("processing");
    try {
      const blob = await rec.stop();
      const ext = extensionFor(blob.type);
      const fd = new FormData();
      fd.append("audio", blob, `voice.${ext}`);
      fd.append("language", "en");
      const { data } = await api.post("/audio/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data?.text || "").trim();
      if (!text) {
        toast.error("Didn't catch that — please try again.");
      } else {
        onTranscript(text);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Voice input failed.");
    } finally {
      recorderRef.current = null;
      setState("idle");
    }
  };

  const sizes = {
    sm: "w-9 h-9",
    md: "w-11 h-11",
    lg: "w-12 h-12",
  };
  const dim = sizes[size] || sizes.sm;
  const iconSize = size === "lg" ? "w-5 h-5" : "w-4 h-4";

  const label =
    state === "recording" ? "Stop recording" :
    state === "processing" ? "Transcribing…" : "Voice input";

  const cls = `${dim} rounded-full flex items-center justify-center transition-colors focus-ring flex-shrink-0 ${
    state === "recording"
      ? "bg-[#ff5a5a] text-white animate-pulse"
      : "text-white/60 hover:text-white hover:bg-white/10"
  } disabled:opacity-30 disabled:cursor-not-allowed`;

  return (
    <button
      type="button"
      onClick={state === "recording" ? stop : start}
      disabled={disabled || state === "processing"}
      className={cls}
      aria-label={label}
      title={label}
      data-testid="mic-btn"
    >
      {state === "processing" ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : state === "recording" ? (
        <Square className={iconSize} />
      ) : (
        <Mic className={iconSize} />
      )}
    </button>
  );
}
